import React, { useState, useEffect } from "react";
import { Button } from "../ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../ui/card";
import { 
  FileText, 
  Shield, 
  AlertTriangle, 
  CheckCircle,
  Loader2,
  Calendar,
  TrendingUp,
  Download
} from "lucide-react";
import { supabase } from "@/lib/supabase";
import { format, subMonths, startOfMonth } from "date-fns";
import { de } from "date-fns/locale";

interface MonthlyReport {
  id: string;
  report_month: string;
  total_checks: number;
  red_checks: number;
  yellow_checks: number;
  green_checks: number;
  top_threats: { category: string; count: number }[] | null;
  generated_at: string | null;
  sent_at: string | null;
}

interface MonthlyReportCardProps {
  userId: string;
  plan: string;
  isVisible: boolean;
}

export function MonthlyReportCard({ userId, plan, isVisible }: MonthlyReportCardProps) {
  const [reports, setReports] = useState<MonthlyReport[]>([]);
  const [loading, setLoading] = useState(false);

  const isFirmaPlan = plan === 'firma';

  const fetchReports = async () => {
    if (!userId) return;
    
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('klartext_monthly_reports')
        .select('*')
        .eq('owner_id', userId)
        .order('report_month', { ascending: false })
        .limit(6);

      if (error) throw error;
      setReports((data || []) as any);
    } catch (error) {
      console.error('Error fetching reports:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isVisible && userId && isFirmaPlan) {
      fetchReports();
    }
  }, [isVisible, userId, isFirmaPlan]);

  if (!isVisible || !isFirmaPlan) return null;

  // Generate current month summary from check history if no report exists yet
  const currentMonth = format(new Date(), 'yyyy-MM-01');

  return (
    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-lg">
          <FileText className="h-5 w-5" />
          Monatliche Sicherheitsreports
        </CardTitle>
        <CardDescription>
          Übersicht über Ihre Sicherheitsanalysen. Reports werden am Monatsende automatisch erstellt.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-6">
            <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
          </div>
        ) : reports.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <FileText className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p className="font-medium">Noch keine Reports vorhanden</p>
            <p className="text-sm mt-1">
              Ihr erster Sicherheitsreport wird am Ende des Monats erstellt.
            </p>
            <div className="mt-4 p-4 bg-blue-50 rounded-lg text-left">
              <h4 className="font-medium text-blue-900 flex items-center gap-2">
                <TrendingUp className="h-4 w-4" />
                Was enthält der Report?
              </h4>
              <ul className="mt-2 text-sm text-blue-800 space-y-1">
                <li>• Anzahl durchgeführter Checks</li>
                <li>• Verteilung nach Gefährdungsstufe</li>
                <li>• Häufigste Bedrohungsarten</li>
                <li>• Trends im Vergleich zum Vormonat</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {reports.map((report) => {
              const monthDate = new Date(report.report_month);
              const total = report.total_checks || 0;
              
              return (
                <div
                  key={report.id}
                  className="p-4 rounded-lg border border-slate-200 bg-white"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-slate-500" />
                      <span className="font-semibold text-slate-900">
                        {format(monthDate, 'MMMM yyyy', { locale: de })}
                      </span>
                    </div>
                    {report.generated_at && (
                      <Button variant="outline" size="sm" className="gap-2" disabled>
                        <Download className="h-4 w-4" />
                        PDF
                      </Button>
                    )}
                  </div>

                  <div className="grid grid-cols-4 gap-3">
                    <div className="text-center p-2 bg-slate-50 rounded">
                      <p className="text-2xl font-bold text-slate-900">{total}</p>
                      <p className="text-xs text-slate-500">Gesamt</p>
                    </div>
                    <div className="text-center p-2 bg-red-50 rounded">
                      <div className="flex items-center justify-center gap-1">
                        <Shield className="h-4 w-4 text-red-600" />
                        <p className="text-2xl font-bold text-red-600">{report.red_checks || 0}</p>
                      </div>
                      <p className="text-xs text-red-600">Gefährlich</p>
                    </div>
                    <div className="text-center p-2 bg-yellow-50 rounded">
                      <div className="flex items-center justify-center gap-1">
                        <AlertTriangle className="h-4 w-4 text-yellow-600" />
                        <p className="text-2xl font-bold text-yellow-600">{report.yellow_checks || 0}</p>
                      </div>
                      <p className="text-xs text-yellow-600">Verdächtig</p>
                    </div>
                    <div className="text-center p-2 bg-green-50 rounded">
                      <div className="flex items-center justify-center gap-1">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                        <p className="text-2xl font-bold text-green-600">{report.green_checks || 0}</p>
                      </div>
                      <p className="text-xs text-green-600">Sicher</p>
                    </div>
                  </div>

                  {report.top_threats && report.top_threats.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-slate-100">
                      <p className="text-xs font-medium text-slate-500 mb-2">Häufigste Bedrohungen:</p>
                      <div className="flex flex-wrap gap-2">
                        {report.top_threats.slice(0, 3).map((threat, i) => (
                          <span
                            key={i}
                            className="px-2 py-1 text-xs bg-slate-100 text-slate-700 rounded-full"
                          >
                            {threat.category} ({threat.count})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
